import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// We use Bearer token auth (not cookies) so it works across the
// preview.static.emergentagent.com <-> preview.emergentagent.com subdomain split
// where browsers + Cloudflare block credentialed cross-origin cookies.
const TOKEN_KEY = "ascend_session_token";
export function setStoredToken(t) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}
export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

axios.interceptors.request.use((config) => {
  const t = getStoredToken();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (!getStoredToken()) {
      setUser(null);
      setOnboarding(null);
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser(r.data.user);
      setOnboarding(r.data.onboarding);
      // Redeem pending referral code (set on landing) — first-call only
      try {
        const pendingRef = sessionStorage.getItem("ascend_ref_code");
        if (pendingRef) {
          sessionStorage.removeItem("ascend_ref_code");
          axios.post(`${API}/referral/redeem`, { code: pendingRef }).catch(() => {});
        }
        // Identify user in PostHog for cohort tracking
        if (typeof window !== "undefined" && window.posthog && r.data.user?.user_id) {
          window.posthog.identify(r.data.user.user_id, {
            email: r.data.user.email,
            name: r.data.user.name,
          });
        }
      } catch (e) {}
    } catch (e) {
      setStoredToken(null);
      setUser(null);
      setOnboarding(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {}
    setStoredToken(null);
    setUser(null);
    setOnboarding(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, onboarding, loading, setUser, setOnboarding, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
